import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg, FIELD, AREA } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/lib/site";
import { PageHero, Reveal, SectionHeading } from "@/components/common/Reveal";
import { TestimonialCard, Stars } from "@/components/common/TestimonialCard";
import { Field } from "@/components/common/Field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", email: "", service: "", rating: 5, message: "" };

const SubmitForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/testimonials", f);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return (
    <div className="lux-card p-12 text-center" data-testid="testimonial-success">
      <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto" />
      <h3 className="mt-5 text-3xl font-medium">Thank you for sharing.</h3>
      <p className="mt-3 text-white/60">Your testimonial will appear once reviewed by our team.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="lux-card p-8 md:p-10 grid sm:grid-cols-2 gap-6" data-testid="testimonial-form">
      <Field label="Name" required><Input className={FIELD} required value={f.name} onChange={set("name")} data-testid="testimonial-name-input" /></Field>
      <Field label="Email (not published)" required><Input className={FIELD} type="email" required value={f.email} onChange={set("email")} data-testid="testimonial-email-input" /></Field>
      <Field label="Service Received">
        <Select value={f.service} onValueChange={set("service")}>
          <SelectTrigger className={FIELD} data-testid="testimonial-service-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Rating" required><div className="h-12 flex items-center"><Stars value={f.rating} onChange={(n) => setF({ ...f, rating: n })} size="w-7 h-7" /></div></Field>
      <Field label="Your Experience" required className="sm:col-span-2"><Textarea className={AREA} required minLength={10} value={f.message} onChange={set("message")} data-testid="testimonial-message-input" /></Field>
      <div className="sm:col-span-2"><button className="btn-gold" disabled={busy} data-testid="testimonial-submit-button">{busy ? "Sending…" : "Submit Testimonial"}</button></div>
    </form>
  );
};

export default function Testimonials() {
  const { data = [] } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then((r) => r.data) });
  return (
    <div data-testid="testimonials-page">
      <PageHero testid="testimonials-hero" eyebrow="Testimonials" title={<>Stories of strategy <span className="italic gold-text">turned success.</span></>} />
      <section className="bg-[#0A0A0A] pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {data.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-list">
              {data.map((t, i) => <Reveal key={t.id} delay={(i % 3) * 0.08}><TestimonialCard t={t} /></Reveal>)}
            </div>
          ) : <p className="text-white/60 text-lg" data-testid="testimonials-empty">Be the first to share your B2R experience.</p>}
        </div>
      </section>
      <section className="bg-black py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Share Your Story" title="Leave a testimonial" sub="Your words help others take their first step toward financial freedom." className="mb-12" />
          <SubmitForm />
        </div>
      </section>
    </div>
  );
}
